import { PaginationParams, PaginatedResponse } from '@scrambl/shared';
import { apiClient } from './api-client';
import { createLogger } from '@/lib/logger';
import { useOrganizationStore } from '@/store/organization.store';
import { isTauri, getPlatform, desktopSecureStorage } from '@/lib/utils/tauri';
import { env } from '@/utils/env';

const logger = createLogger('lib/api/base-api');

export interface CrudOperations<T, CreateT, UpdateT> {
  list(params?: Partial<PaginationParams>): Promise<PaginatedResponse<T>>;
  findById(id: string): Promise<T>;
  create(data: CreateT): Promise<T>;
  update(id: string, data: UpdateT): Promise<T>;
  delete(id: string): Promise<void>;
}

export interface BaseApiConfig {
  baseEndpoint: string;
  responseNormalization?: boolean;
}

export class BaseApi<T = unknown, CreateT = unknown, UpdateT = unknown> implements CrudOperations<T, CreateT, UpdateT> {
  protected config: Required<BaseApiConfig>;

  constructor(config: BaseApiConfig) {
    this.config = {
      responseNormalization: true,
      ...config
    };
  }

  // Query parameter building
  protected buildQueryParams(params: Record<string, unknown>): string {
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, String(value));
      }
    });
    
    return queryParams.toString() ? `?${queryParams.toString()}` : '';
  }

  // Response normalization
  protected normalizeResponse<R>(response: R | { data: R }): R {
    if (!this.config.responseNormalization) return response as R;
    
    if (response && typeof response === 'object') {
      // If response has a data property, return it
      if ('data' in response && response.data) {
        return response.data as R;
      }
      
      // If response is directly the object we want, return it
      return response as R;
    }
    
    return response as R;
  }

  // Standard CRUD operations
  async list(params: Partial<PaginationParams> = {}): Promise<PaginatedResponse<T>> {
    const queryString = this.buildQueryParams(params);
    const endpoint = `${this.config.baseEndpoint}${queryString}`;

    try {
      const response = await apiClient<PaginatedResponse<T>>(endpoint);
      return response;
    } catch (error) {
      logger.error(`[BaseApi] Error fetching ${this.config.baseEndpoint}:`, error);
      throw error;
    }
  }

  async getModuleCounts(): Promise<Record<string, number>> {
    const endpoint = `${this.config.baseEndpoint}/module-counts`;

    try {
      const response = await apiClient<Record<string, number>>(endpoint);
      return response;
    } catch (error) {
      logger.error(`[BaseApi] Error fetching module counts for ${this.config.baseEndpoint}:`, error);
      throw error;
    }
  }

  async findById(id: string): Promise<T> {
    const endpoint = `${this.config.baseEndpoint}/${id}`;

    try {
      const response = await apiClient<T | { data: T }>(endpoint);
      const normalized = this.normalizeResponse(response);
      return normalized;
    } catch (error) {
      logger.error(`[BaseApi] Error fetching ${this.config.baseEndpoint}/${id}:`, error);
      throw error;
    }
  }

  async create(data: CreateT): Promise<T> {
    try {
      const response = await apiClient<T | { data: T }>(this.config.baseEndpoint, {
        method: 'POST',
        body: data
      });

      const normalized = this.normalizeResponse(response);
      return normalized;
    } catch (error) {
      logger.error(`[BaseApi] Error creating ${this.config.baseEndpoint}:`, error);
      throw error;
    }
  }

  async update(id: string, data: UpdateT): Promise<T> {
    try {
      const response = await apiClient<T | { data: T }>(`${this.config.baseEndpoint}/${id}`, {
        method: 'PUT',
        body: data
      });

      const normalized = this.normalizeResponse(response);
      return normalized;
    } catch (error) {
      logger.error(`[BaseApi] Error updating ${this.config.baseEndpoint}/${id}:`, error);
      throw error;
    }
  }

  async delete(id: string): Promise<void> {
    try {
      await apiClient<void>(`${this.config.baseEndpoint}/${id}`, {
        method: 'DELETE'
      });
    } catch (error) {
      logger.error(`[BaseApi] Error deleting ${this.config.baseEndpoint}/${id}:`, error);
      throw error;
    }
  }

  // Utility methods for extending classes
  protected async makeRequest<R>(endpoint: string, options: Record<string, unknown> = {}): Promise<R> {
    return apiClient<R>(endpoint, options);
  }

  /**
   * Make a request that returns binary data (Blob).
   * Use this for PDF exports, file downloads, etc.
   * Handles authentication and org context the same way as makeRequest.
   */
  protected async makeBlobRequest(
    endpoint: string,
    options: {
      method?: string;
      headers?: Record<string, string>;
      acceptType?: string;
      body?: unknown;
    } = {}
  ): Promise<Blob> {
    const { currentOrganization, externalOrgRelationship } = useOrganizationStore.getState();

    const headers: Record<string, string> = {
      'Accept': options.acceptType || 'application/octet-stream',
      'x-platform': getPlatform(),
      ...options.headers,
    };

    if (options.body !== undefined && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json'
    }

    // Add organization context headers
    if (currentOrganization?.id) {
      headers['x-organization-id'] = currentOrganization.id;
      if (currentOrganization.slug) {
        headers['x-organization-slug'] = currentOrganization.slug;
      }
      if (
        currentOrganization.isExternalAccess === true &&
        externalOrgRelationship?.id
      ) {
        headers['x-external-org-relationship-id'] = externalOrgRelationship.id;
      }
    }

    // Add desktop auth header if running in Tauri
    if (isTauri()) {
      try {
        const tokens = await desktopSecureStorage.getTokens();
        if (tokens?.accessToken) {
          headers['Authorization'] = `Bearer ${tokens.accessToken}`;
        }
      } catch (error) {
        logger.error('[BaseApi] Failed to get desktop auth token:', error);
      }
    }

    const response = await fetch(
      `${env.apiUrl}${endpoint}`,
      {
        method: options.method || 'GET',
        credentials: 'include',
        headers,
        body:
          options.body !== undefined
            ? JSON.stringify(options.body)
            : undefined,
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Request failed: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return response.blob();
  }

  protected addToOrganizationData<D extends Record<string, unknown>>(data: D, organizationId?: string): D & { organizationId?: string } {
    if (organizationId) {
      return { ...data, organizationId };
    }
    return data;
  }
}

// Singleton base class for APIs that need singleton pattern
export class SingletonBaseApi<T = unknown, CreateT = unknown, UpdateT = unknown> extends BaseApi<T, CreateT, UpdateT> {
  private static instances = new Map<string, SingletonBaseApi<unknown, unknown, unknown>>();
  
  protected constructor(config: BaseApiConfig, instanceKey: string) {
    super(config);
    
    if (SingletonBaseApi.instances.has(instanceKey)) {
      const existing = SingletonBaseApi.instances.get(instanceKey)!;
      Object.setPrototypeOf(this, Object.getPrototypeOf(existing));
      Object.assign(this, existing);
      return;
    }
    
    SingletonBaseApi.instances.set(instanceKey, this as SingletonBaseApi<unknown, unknown, unknown>);
  }

  static getInstance<T, CreateT, UpdateT>(
    config: BaseApiConfig,
    instanceKey: string,
    constructor: new (config: BaseApiConfig, instanceKey: string) => SingletonBaseApi<T, CreateT, UpdateT>
  ): SingletonBaseApi<T, CreateT, UpdateT> {
    if (!SingletonBaseApi.instances.has(instanceKey)) {
      new constructor(config, instanceKey);
    }
    return SingletonBaseApi.instances.get(instanceKey)! as SingletonBaseApi<T, CreateT, UpdateT>;
  }
}

// Factory for creating consistent API instances
export function createApiInstance<T, CreateT, UpdateT>(
  config: BaseApiConfig
): BaseApi<T, CreateT, UpdateT> {
  return new BaseApi<T, CreateT, UpdateT>(config);
}

/**
 * Builds a URL query string from parameters.
 * Filters out undefined, null, and empty string values.
 * @param params - Key-value pairs to convert to query string
 * @returns Query string with leading '?' or empty string if no params
 */
export function buildQueryString<T extends object>(params: T): string {
  const queryParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, String(value));
    }
  });

  return queryParams.toString() ? `?${queryParams.toString()}` : '';
}

// Type helpers for API responses
export type ApiResponse<T> = T | { data: T };
export type ApiListResponse<T> = PaginatedResponse<T>;
export type ApiCreateResponse<T> = T | { data: T };
export type ApiUpdateResponse<T> = T | { data: T }; 
